import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { MoreVertical, Mail, Calendar, Shield, Building2, Eye } from 'lucide-react';
import { UnifiedUser } from '@/hooks/useUnifiedUsers';
import { format } from 'date-fns';

interface UserCardProps {
  user: UnifiedUser;
  currentUserId?: string;
  onEdit?: (user: UnifiedUser) => void;
  onDelete?: (userId: string) => void;
  onToggleActive?: (userId: string, isActive: boolean) => void;
  onResetPassword?: (email: string) => void;
  onSetTenant?: (userId: string) => void;
  onAssignProjects?: (userId: string) => void;
  onImpersonate?: (userId: string) => void;
}

export const UserCard = ({ 
  user, 
  currentUserId,
  onEdit, 
  onDelete, 
  onToggleActive, 
  onResetPassword, 
  onSetTenant,
  onAssignProjects,
  onImpersonate,
}: UserCardProps) => {
  const getInitials = () => {
    if (user.display_name) {
      return user.display_name.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    return user.email.substring(0, 2).toUpperCase();
  };

  const getRoleBadgeVariant = (role: string) => {
    if (role === 'super_admin') return 'default';
    if (role === 'admin') return 'secondary';
    if (role === 'charging_user') return 'outline';
    return 'outline';
  };

  const getUserTypeLabel = () => {
    return user.user_type === 'crm' ? 'CRM' : 'Portal';
  };

  const getUserTypeBadgeVariant = () => {
    return user.user_type === 'crm' ? 'default' : 'secondary';
  };

  return (
    <Card className="p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <Avatar className="h-12 w-12">
          <AvatarImage src={user.avatar_url || undefined} />
          <AvatarFallback className="bg-primary/10 text-primary">
            {getInitials()}
          </AvatarFallback>
        </Avatar>

        {/* User Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="font-medium text-foreground truncate">
                {user.display_name || user.email.split('@')[0]}
              </h3>
              <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                <Mail className="h-3 w-3" />
                <span className="truncate">{user.email}</span>
              </div>
            </div>

            {/* Actions Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onEdit && (
                  <DropdownMenuItem onClick={() => onEdit(user)}>
                    Bewerken
                  </DropdownMenuItem>
                )}
                {user.user_type === 'crm' && onResetPassword && (
                  <DropdownMenuItem onClick={() => onResetPassword(user.email)}>
                    Wachtwoord resetten
                  </DropdownMenuItem>
                )}
                {user.user_type === 'crm' && onSetTenant && (
                  <DropdownMenuItem onClick={() => onSetTenant(user.id)}>
                    Tenant instellen
                  </DropdownMenuItem>
                )}
                {user.user_type === 'crm' && onImpersonate && user.id !== currentUserId && (
                  <DropdownMenuItem onClick={() => onImpersonate(user.id)}>
                    <Eye className="h-4 w-4 mr-2" />
                    Inloggen als gebruiker
                  </DropdownMenuItem>
                )}
                {user.user_type === 'portal' && onToggleActive && (
                  <DropdownMenuItem onClick={() => onToggleActive(user.id, user.is_active)}>
                    {user.is_active ? 'Deactiveren' : 'Activeren'}
                  </DropdownMenuItem>
                )}
                {user.user_type === 'portal' && user.roles.includes('inspector') && onAssignProjects && (
                  <DropdownMenuItem onClick={() => onAssignProjects(user.id)}>
                    Projecten toewijzen
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => onDelete(user.id)}
                      className="text-destructive"
                    >
                      Verwijderen
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Badges & Meta */}
          <div className="flex flex-wrap gap-2 mt-3">
            <Badge variant={getUserTypeBadgeVariant()}>
              {getUserTypeLabel()}
            </Badge>
            {user.roles.map(role => (
              <Badge key={role} variant={getRoleBadgeVariant(role)}>
                <Shield className="h-3 w-3 mr-1" />
                {role}
              </Badge>
            ))}
            {!user.is_active && (
              <Badge variant="destructive">Inactief</Badge>
            )}
          </div>

          {/* Additional Info */}
          <div className="flex flex-wrap gap-4 mt-3 text-xs text-muted-foreground">
            {user.tenant_name && (
              <div className="flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                <span>{user.tenant_name}</span>
              </div>
            )}
            {user.last_login_at && (
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>Laatst ingelogd: {format(new Date(user.last_login_at), 'dd-MM-yyyy')}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
